import { useState, useEffect, useCallback } from "react";
import { getAllVersions } from "../services/versions";
import { getAllFeatures } from "../services/features";
import { getAllBugs } from "../services/bugs";
import { getAllCommits } from "../services/commits";
import type { Version, FeatureRequest, BugReport, Commit } from "../types";

export default function Releases() {
  const [versions, setVersions] = useState<Version[]>([]);
  const [features, setFeatures] = useState<FeatureRequest[]>([]);
  const [bugs, setBugs] = useState<BugReport[]>([]);
  const [commits, setCommits] = useState<Commit[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [v, f, b, c] = await Promise.all([
        getAllVersions(),
        getAllFeatures(),
        getAllBugs(),
        getAllCommits(),
      ]);
      setVersions(v);
      setFeatures(f);
      setBugs(b);
      setCommits(c);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Releases are versions that have commits, linked items, or reached prod
  const releases = versions.filter(
    (v) => v.commits.length > 0 || v.linkedFeatures.length > 0 || v.linkedBugs.length > 0 || v.stage === "prod"
  );

  function getReleaseNotes(version: Version) {
    const linkedFeatures = features.filter((f) => version.linkedFeatures.includes(f.id));
    const linkedBugs = bugs.filter((b) => version.linkedBugs.includes(b.id));
    const linkedCommits = commits.filter((c) => version.commits.includes(c.commitId));
    return { features: linkedFeatures, bugs: linkedBugs, commits: linkedCommits };
  }

  if (loading && versions.length === 0) {
    return <div className="page"><p>Loading releases...</p></div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>Releases</h2>
        <p>Release notes for each version — features shipped, bugs fixed, and all commits included.</p>
      </div>

      {releases.length === 0 ? (
        <div className="card">
          <p style={{ color: "var(--text-muted)", textAlign: "center" }}>
            No releases yet. Create a version from commits to generate release notes.
          </p>
        </div>
      ) : (
        releases.map((v) => {
          const { features: vFeatures, bugs: vBugs, commits: vCommits } = getReleaseNotes(v);
          return (
            <div key={v.id} className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h3 style={{ display: "inline" }}>v{v.version}</h3>
                  <span
                    className={`badge ${v.stage === "prod" ? "badge-success" : v.stage === "beta" ? "badge-warning" : "badge-info"}`}
                    style={{ marginLeft: "0.75rem" }}
                  >
                    {v.stage}
                  </span>
                  {v.status === "rolled-back" && (
                    <span className="badge badge-danger" style={{ marginLeft: "0.5rem" }}>rolled back</span>
                  )}
                </div>
                <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                  {new Date(v.createdAt).toLocaleString()}
                </span>
              </div>

              {/* Auto-generated changelog */}
              {v.changelog && (
                <pre style={{ marginTop: "0.75rem", fontSize: "0.85rem", whiteSpace: "pre-wrap", color: "var(--text-muted)", background: "var(--bg)", padding: "0.75rem", borderRadius: "6px" }}>
                  {v.changelog}
                </pre>
              )}

              <div style={{ marginTop: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                {vFeatures.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                      ✨ Features ({vFeatures.length})
                    </h4>
                    <ul style={{ paddingLeft: "1.25rem", fontSize: "0.9rem" }}>
                      {vFeatures.map((f) => (
                        <li key={f.id} style={{ marginBottom: "0.25rem" }}>{f.title}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {vBugs.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                      🐛 Bug Fixes ({vBugs.length})
                    </h4>
                    <ul style={{ paddingLeft: "1.25rem", fontSize: "0.9rem" }}>
                      {vBugs.map((b) => (
                        <li key={b.id} style={{ marginBottom: "0.25rem" }}>{b.title}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Commits in this version */}
              {vCommits.length > 0 && (
                <div style={{ marginTop: "1rem" }}>
                  <h4 style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                    📝 Commits ({vCommits.length})
                  </h4>
                  <div style={{ fontSize: "0.85rem" }}>
                    {vCommits.map((c) => (
                      <div key={c.commitId} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
                        <code style={{ flexShrink: 0 }}>{c.commitId.substring(0, 7)}</code>
                        <span>{c.message.split("\n")[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
